import { Component } from 'react';
import { defineMessages, IntlContext, FormattedMessage } from 'dibs-react-intl';
import getter from 'dibs-object-getter';
import scrollIntoView from 'scroll-into-view';
import { getBuyerId, getEverLoggedIn } from 'dibs-cookie-jar';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { Banner } from './WorkspaceMatchmaker/Banner.jsx';
import { HowItWorks } from './WorkspaceMatchmaker/HowItWorks.jsx';
import { FormStepContacts } from './WorkspaceMatchmaker/FormStepContacts.jsx';
import { FormStepPreference } from './WorkspaceMatchmaker/FormStepPreference.jsx';
import { FormStepInspiration } from './WorkspaceMatchmaker/FormStepInspiration.jsx';
import { FormConfirmation } from './WorkspaceMatchmaker/FormConfirmation.jsx';
import formStyles from './WorkspaceMatchmaker/styles/FormGeneral.scss';
import { postToFA } from './postToFA.js';
import { zipCodeLookup } from '../designProfile/helpers/zipCodeLookup.ts';
import { getEnvironment } from '../relayModernEnvironment.ts';
import { addLoginHandlers } from 'dibs-buyer-layout/src/utils/loginHandlers.ts';
import { userLookup } from './queries/userLookup.js';
import { Loader } from '../shared/Loader.tsx';
import { logError } from '../helpers.ts';
import { designerConnectionLogin, designerConnectionRegistration } from '../authInterface.ts';
import {
    trackPageview,
    trackGetStartedClick,
    trackNextClick,
    trackNotMatchingClick,
    trackSendInformationClick,
    trackLoginEntry,
    trackLoginComplete,
} from './matchmakerTracking.js';
const FORM_STEP_PREFERENCE = 'Preference';
const FORM_STEP_CONTACTS = 'Contacts';
const FORM_STEP_INSPIRATION = 'Inspiration';
const FORM_STEP_CONFIRMATION = 'Confirmation';
const messages = defineMessages({
    budget: {
        id: 'abt.matchmaker.MatchmakerContainer.requiredField_errorBudget',
        defaultMessage: 'Please select your budget',
    },
    postalCode: {
        id: 'abt.matchmaker.MatchmakerContainer.requiredField_errorPostalCode',
        defaultMessage: 'Please enter a valid postal or zip code',
    },
    country: {
        id: 'abt.matchmaker.MatchmakerContainer.requiredField_errorCountry',
        defaultMessage: 'Please select a country',
    },
    projectScopes: {
        id: 'abt.matchmaker.MatchmakerContainer.requiredField_errorService',
        defaultMessage: 'Please select at least one design service',
    },
    phone: {
        id: 'abt.matchmaker.MatchmakerContainer.requiredField_errorPhone',
        defaultMessage: 'Please enter a valid phone number',
    },
    email: {
        id: 'abt.matchmaker.MatchmakerContainer.requiredField_errorEmail',
        defaultMessage: 'Please enter a valid email address',
    },
    layer: {
        id: 'abt.matchmaker.MatchmakerContainer.requiredField_errorStyle',
        defaultMessage: 'Please select your style',
    },
    color: {
        id: 'abt.matchmaker.MatchmakerContainer.requiredField_errorColor',
        defaultMessage: 'Please select your color palette',
    },
    time: {
        id: 'abt.matchmaker.MatchmakerContainer.requiredField_errorTime',
        defaultMessage: 'Please select how you want to design your space',
    },
    selectedPhotos: {
        id: 'abt.matchmaker.MatchmakerContainer.requiredField_errorPhotos',
        defaultMessage: 'Please select one or more images',
    },
});
const validatedFieldsStepContacts = ['budget', 'postalCode', 'country', 'phone'];
const validatedFieldsStepContactsNotLoggedIn = [
    'budget',
    'postalCode',
    'country',
    'phone',
    'email',
];
const validatedFieldsStepPreference = ['time', 'color', 'layer', 'projectScopes'];
const validatedFieldsStepInspiration = ['selectedPhotos'];
const validatedFieldsProceedToPhoto = ['projectScopes'];
const bannerPreferenceTitle = (
    <FormattedMessage
        id="abt.matchmaker.formStepPreference.bannerTitle"
        defaultMessage="Designer Connection Service"
    />
);
const bannerPreferenceSubText = (
    <FormattedMessage
        id="abt.matchmaker.formStepPreference.headerMessage"
        defaultMessage="Looking to redesign your space? Get started by letting us know your project and style preferences below."
    />
);
const bannerContactsTitle = (
    <FormattedMessage
        id="abt.matchmaker.formStepContacts.step1Title"
        defaultMessage="Great! Please tell us more about your project"
    />
);
const bannerContactsSubText = (
    <FormattedMessage
        id="abt.matchmaker.formStepContacts.step1Subtitle"
        defaultMessage="We'll use this information to match you to a designer who best suits your project needs"
    />
);
const bannerInspirationTitle = (
    <FormattedMessage
        id="abt.matchmaker.formStepInspiration.title"
        defaultMessage="What Images Inspire You?"
    />
);
const bannerInspirationSubText = (
    <FormattedMessage
        id="abt.matchmaker.formStepInspiration.subText"
        defaultMessage="Select as many images as you like to help us determine your style."
    />
);
export class MatchmakerContainer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            onPage: FORM_STEP_PREFERENCE,
            country: '',
            budget: '',
            phone: '',
            postalCode: '',
            projectScopes: [],
            time: null,
            color: null,
            layer: null,
            errors: {},
            tellUsMore: '',
            selectedPhotos: [],
            email: '',
            locationDisplay: '',
            userLookupComplete: false,
            userId: getBuyerId(document.cookie),
            firstName: '',
            lastName: '',
            reviewBack: FORM_STEP_CONTACTS,
        };
    }

    componentDidMount() {
        this.setUserData();
        trackPageview();
        addLoginHandlers(() => {
            const newUserId = getBuyerId(document.cookie);

            if (newUserId !== this.state.userId) {
                this.setState(
                    {
                        userId: newUserId,
                    },
                    this.setUserData
                );
            }
        });
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.onPage !== prevState.onPage) {
            trackPageview();
        }
    }

    static contextType = IntlContext;
    setUserData = () => {
        const { userId } = this.state;

        if (userId) {
            userLookup(userId)
                .then(userData => {
                    this.setState({
                        email: userData.email || '',
                        firstName: userData.firstName || '',
                        lastName: userData.lastName || '',
                        userLookupComplete: true,
                    });
                })
                .catch(error => {
                    logError(error);
                    this.setState({
                        userLookupComplete: true,
                    });
                });
        } else {
            this.setState({
                userLookupComplete: true,
            });
        }
    };
    getFieldErrors = (fieldName, value) => {
        const validationResult = this.validate(fieldName, {
            [fieldName]: value,
        });
        return Object.assign({}, this.state.errors, {
            [fieldName]: validationResult[fieldName],
        });
    };
    onChangeBudget = newBudget => {
        const budget = newBudget.currentTarget.value;
        const errors = this.getFieldErrors('budget', budget);
        this.setState({
            budget,
            errors,
        });
    };
    onChangeCountry = newCountry => {
        const country = newCountry.currentTarget.value;
        const errors = this.getFieldErrors('country', country);
        this.setState({
            country,
            errors,
        });
    };
    onChangePostalCode = newPostalCode => {
        const postalCode = newPostalCode.currentTarget.value;
        const errors = this.getFieldErrors('postalCode', postalCode);
        this.setState({
            postalCode,
            errors,
        });
    };
    onChangeEmail = newEmail => {
        const email = newEmail.currentTarget.value;
        const errors = this.getFieldErrors('email', email);
        this.setState({
            email,
            errors,
        });
    };
    onChangePhone = newPhone => {
        const phone = newPhone.currentTarget.value;
        const errors = this.getFieldErrors('phone', phone);
        this.setState({
            phone,
            errors,
        });
    };
    onChangeProjectScope = scope => () => {
        const scopeIndex = this.state.projectScopes.indexOf(scope);
        let projectScopes = this.state.projectScopes.slice();

        if (scopeIndex === -1) {
            projectScopes.push(scope);
        } else if (scopeIndex > -1) {
            projectScopes = this.state.projectScopes.filter(value => value !== scope);
        }

        const errors = this.getFieldErrors('projectScopes', projectScopes);
        this.setState({
            projectScopes,
            errors,
        });
    };
    onChangeTime = time => {
        const errors = this.getFieldErrors('time', time);
        this.setState({
            time,
            errors,
        });
    };
    onChangeColor = color => {
        const errors = this.getFieldErrors('color', color);
        this.setState({
            color,
            errors,
        });
    };
    onChangeLayer = layer => {
        const errors = this.getFieldErrors('layer', layer);
        this.setState({
            layer,
            errors,
        });
    };
    onChangeMore = newInfo => {
        this.setState({
            tellUsMore: newInfo.currentTarget.value,
        });
    };
    validate = (validationFields, payload) => {
        const intl = this.context;
        const fields = Array.isArray(validationFields) ? validationFields : [validationFields];
        return fields.reduce((acc, field) => {
            let message = {};

            if (field === 'phone') {
                const phoneRegex = new RegExp(/^[0-9\.\-\/\(\)\+]+$/);
                const isValidField = phoneRegex.test(payload[field]);

                if (!isValidField) {
                    message = {
                        [field]: intl.formatMessage(messages[field]),
                    };
                }
            }

            if (field === 'email') {
                const emailRegex = new RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,100}$/i);
                const isValidField = emailRegex.test(payload[field]);

                if (!isValidField) {
                    message = {
                        [field]: intl.formatMessage(messages[field]),
                    };
                }
            }

            if (!payload[field] || payload[field].length === 0) {
                message = {
                    [field]: intl.formatMessage(messages[field]),
                };
            }

            return Object.assign(acc, message);
        }, {});
    };
    scrollToElement = el => {
        if (el) {
            scrollIntoView(el, {
                time: 1000,
                align: {
                    top: 0,
                },
            });
        }
    };
    scrollFormIntoView = () => {
        const scrollTarget = document.getElementById('jsScrollTargetForm');

        if (scrollTarget) {
            this.scrollToElement(scrollTarget);
        }
    };
    scrollToInitialStep = () => {
        const el = document.getElementById('jsScrollTargetMoreInfo');

        if (el) {
            this.scrollToElement(el);
        }
    };
    scrollDown = () => {
        trackGetStartedClick();
        this.scrollToInitialStep();
    };
    scrollToError = err => {
        const error = Object.keys(err);

        if (error.length > 0) {
            let scrollTarget = null;

            if (error.includes('projectScopes')) {
                scrollTarget = 'jsScrollTargetScopePreference';
            } else if (error.includes('time')) {
                scrollTarget = 'jsScrollTargetTimePreference';
            } else if (error.includes('color')) {
                scrollTarget = 'jsScrollTargetColorPreference';
            } else if (error.includes('layer')) {
                scrollTarget = 'jsScrollTargetLayerPreference';
            } else if (this.state.onPage === 'Contacts') {
                scrollTarget = 'jsScrollTargetForm';
            }

            this.scrollToElement(document.getElementById(scrollTarget));
        }
    };
    onPageChange = (newPage, options = {}) => {
        const { pageBack, reviewBack, shouldSubmitForm, validatedFields, trackGA } = options || {};

        if (pageBack) {
            this.setState(
                {
                    onPage: newPage,
                },
                () => {
                    this.scrollFormIntoView();
                }
            );
            return;
        }

        const completedPage = this.state.onPage;

        if (validatedFields) {
            const validationErrors = this.validate(validatedFields, this.state);

            if (Object.keys(validationErrors).length) {
                this.setState(
                    {
                        errors: validationErrors,
                    },
                    () => {
                        this.scrollToError(this.state.errors);
                    }
                );
                return;
            }
        }

        const newStateSlice = {
            onPage: newPage,
            errors: {},
        };

        if (reviewBack) {
            newStateSlice.reviewBack = reviewBack;
        }

        this.setState(newStateSlice, () => {
            if (trackGA) {
                trackGA();
            }

            if (completedPage === FORM_STEP_PREFERENCE) {
                this.handleLocationUpdate();
            }

            this.scrollFormIntoView();

            if (shouldSubmitForm) {
                const formCompleted = completedPage === FORM_STEP_CONTACTS;
                this.submitForm(formCompleted);
            }
        });
    };
    handleLocationUpdate = () => {
        const { postalCode: zipCode, country } = this.state;

        if (!zipCode || !country) {
            return;
        }

        zipCodeLookup(getEnvironment(), {
            zipCode,
            country,
        }).then(lookupResults => {
            const result = getter(lookupResults).at('viewer').at('zipCode').value(0);

            if (result) {
                const { city, state } = result;
                let cityStateDisplay = city;

                if (state) {
                    cityStateDisplay = `${city}, ${state}`;
                }

                const locationDisplay = `${cityStateDisplay}, ${zipCode}, ${country}`;
                this.setState({
                    locationDisplay,
                });
            }
        });
    };
    submitForm = formCompleted => {
        const { email, userId } = this.state;
        const formState = {
            budget: this.state.budget,
            color: this.state.color,
            country: this.state.country,
            email,
            firstName: this.state.firstName,
            lastName: this.state.lastName,
            layer: this.state.layer,
            phone: this.state.phone,
            postalCode: this.state.postalCode,
            projectScopes: this.state.projectScopes,
            selectedPhotos: this.state.selectedPhotos,
            tellUsMore: this.state.tellUsMore,
            time: this.state.time,
            userId,
        };

        if (formCompleted) {
            if (userId) {
                this.onPageChange(FORM_STEP_CONFIRMATION);
                postToFA({
                    state: formState,
                    shouldTrackGA: true,
                });
            } else {
                const everLoggedIn = getEverLoggedIn(document.cookie);
                const label = 'designer-connection';

                if (everLoggedIn) {
                    trackLoginEntry();
                    designerConnectionLogin({
                        email,
                        label,
                        onSuccess: trackLoginComplete,
                    });
                } else {
                    designerConnectionRegistration({
                        email,
                        label,
                    });
                }
            }
        } else {
            postToFA({
                state: formState,
            });
        }
    };
    updatePhotoSelection = index => {
        const newSelectedPhotos = this.state.selectedPhotos.slice();
        const indexInArray = this.state.selectedPhotos.indexOf(index);

        if (indexInArray > -1) {
            newSelectedPhotos.splice(indexInArray, 1);
        } else {
            newSelectedPhotos.push(index);
        }

        const errors = Object.assign({}, this.state.errors, {
            selectedPhotos: this.validate(
                ['selectedPhotos'],
                Object.assign({}, this.state, {
                    selectedPhotos: newSelectedPhotos,
                })
            ).selectedPhotos,
        });
        this.setState({
            selectedPhotos: newSelectedPhotos,
            errors,
        });
    };

    render() {
        const { userLookupComplete } = this.state;
        let body = null;

        if (!userLookupComplete) {
            return (
                <div className={formStyles.loaderContainer}>
                    <Loader />
                </div>
            );
        }

        switch (this.state.onPage) {
            case FORM_STEP_PREFERENCE:
                body = (
                    <>
                        <div
                            id="jsScrollTargetForm"
                            className={formStyles.item}
                            data-tn="preference-form"
                        >
                            <Banner
                                header={bannerPreferenceTitle}
                                subheader={bannerPreferenceSubText}
                                page="page1"
                                classNames={formStyles.bannerPreferenceSubheader}
                                button={
                                    <div className={formStyles.ctaButton}>
                                        <Button
                                            onClick={this.scrollDown}
                                            type="secondary"
                                            dataTn="matchmaker-howItWorks"
                                            size="small"
                                            fullWidth={false}
                                        >
                                            <FormattedMessage
                                                id="abt.matchmaker.formStepContacts.scrollToStart"
                                                defaultMessage="How It Works"
                                            />
                                        </Button>
                                    </div>
                                }
                            />
                            <FormStepPreference
                                onChangeProjectScope={this.onChangeProjectScope}
                                projectScopes={this.state.projectScopes}
                                color={this.state.color}
                                onChangeColor={this.onChangeColor}
                                layer={this.state.layer}
                                onChangeLayer={this.onChangeLayer}
                                time={this.state.time}
                                onChangeTime={this.onChangeTime}
                                onPageChange={() => {
                                    this.onPageChange(FORM_STEP_CONTACTS, {
                                        shouldSubmitForm: true,
                                        validatedFields: validatedFieldsStepPreference,
                                        trackGA: trackNextClick,
                                    });
                                }}
                                goToPhotoSelect={() => {
                                    this.onPageChange(FORM_STEP_INSPIRATION, {
                                        shouldSubmitForm: true,
                                        validatedFields: validatedFieldsProceedToPhoto,
                                        trackGA: trackNotMatchingClick,
                                    });
                                }}
                                errors={this.state.errors}
                            />
                        </div>
                        <HowItWorks />
                    </>
                );
                break;

            case FORM_STEP_CONTACTS:
                body = (
                    <>
                        <div
                            id="jsScrollTargetForm"
                            className={formStyles.item}
                            data-tn="contact-form"
                        >
                            <Banner
                                header={bannerContactsTitle}
                                subheader={bannerContactsSubText}
                                page="page2"
                                classNames={formStyles.bannerContactsSubheader}
                            />
                            <FormStepContacts
                                budget={this.state.budget}
                                onChangeBudget={this.onChangeBudget}
                                postalCode={this.state.postalCode}
                                onChangePostalCode={this.onChangePostalCode}
                                country={this.state.country}
                                onChangeCountry={this.onChangeCountry}
                                phone={this.state.phone}
                                onChangePhone={this.onChangePhone}
                                email={this.state.email}
                                onChangeEmail={this.onChangeEmail}
                                onChangeMore={this.onChangeMore}
                                tellUsMore={this.state.tellUsMore}
                                onPageChange={() => {
                                    this.onPageChange(FORM_STEP_CONTACTS, {
                                        reviewBack: FORM_STEP_PREFERENCE,
                                        validatedFields: this.state.userId
                                            ? validatedFieldsStepContacts
                                            : validatedFieldsStepContactsNotLoggedIn,
                                        shouldSubmitForm: true,
                                        trackGA: trackSendInformationClick,
                                    });
                                }}
                                onPageBack={() => {
                                    this.onPageChange(FORM_STEP_PREFERENCE, {
                                        pageBack: true,
                                    });
                                }}
                                errors={this.state.errors}
                                userId={this.state.userId}
                            />
                        </div>
                        <HowItWorks />
                    </>
                );
                break;

            case FORM_STEP_INSPIRATION:
                body = (
                    <>
                        <div id="jsScrollTargetForm" className={formStyles.item}>
                            <Banner
                                header={bannerInspirationTitle}
                                subheader={bannerInspirationSubText}
                                page="page1"
                                classNames={formStyles.bannerInspirationSubheader}
                            />
                            <div className={formStyles.contentWrapper}>
                                <FormStepInspiration
                                    selectedPhotos={this.state.selectedPhotos}
                                    onPageChange={() => {
                                        this.onPageChange(FORM_STEP_CONTACTS, {
                                            reviewBack: FORM_STEP_INSPIRATION,
                                            shouldSubmitForm: true,
                                            validatedFields: validatedFieldsStepInspiration,
                                            trackGA: trackNextClick,
                                        });
                                    }}
                                    updatePhotoSelection={this.updatePhotoSelection}
                                    onPageBack={() => {
                                        this.onPageChange(FORM_STEP_PREFERENCE, {
                                            pageBack: true,
                                        });
                                    }}
                                    errors={this.state.errors}
                                />
                            </div>
                        </div>
                        <HowItWorks />
                    </>
                );
                break;

            case FORM_STEP_CONFIRMATION:
                body = (
                    <div id="jsScrollTargetForm" className={formStyles.item}>
                        <FormConfirmation />
                    </div>
                );
                break;
        }

        return (
            <div className={formStyles.container}>
                <div className={formStyles.header} />
                <div className={formStyles.wrapper}>{body}</div>
            </div>
        );
    }
}
